// ============================================================================
// MODULAR DESIGN PRINCIPLES (MANDATORY)
// ============================================================================
// This file strictly follows:
// 1. Single Responsibility Principle - Each component does ONE thing
// 2. Separation of Concerns - Logic separated from presentation
// 3. Modular Architecture - Independent, reusable components
//
// Refer to: docs/architecture/modular-design-standards.md
// ============================================================================

// Refer to docs/implementation/voice-analysis-worker-implementation.md

import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Chip,
  Paper,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
  Divider,
  Stack,
  IconButton,
  Tooltip,
  Button,
} from '@mui/material';
import {
  ExpandMore,
  ThumbUp,
  ThumbDown,
  TrendingFlat,
  CheckCircle,
  Cancel,
  Info as InfoIcon,
  EmojiEmotions,
  Psychology,
} from '@mui/icons-material';
// ChatGPT deprecated - removed AutoAwesome icon
import QualificationEvidenceDisplay from './QualificationEvidenceDisplay';
import ScoreOverrideDialog from './ScoreOverrideDialog';
import { useAuth } from '../contexts/AuthContext';

/**
 * Map a metric JSON row to a category breakdown row.
 * Avoid strict === on category_id alone — stored metrics often omit or stringify ids differently.
 */
function metricBelongsToOllamaCategory(metric, category) {
  const mid = metric?.category_id;
  const cid = category?.category_id;
  if (mid != null && cid != null && String(mid) === String(cid)) {
    return true;
  }
  const k1 = metric?.category_key;
  const k2 = category?.category_key;
  if (k1 != null && k2 != null && String(k1).trim() === String(k2).trim()) {
    return true;
  }
  const n1 = metric?.category_name;
  const n2 = category?.category_name;
  return (
    n1 != null &&
    n2 != null &&
    String(n1).trim() === String(n2).trim()
  );
}

const AnalysisResults = ({
  analysisData,
  onReferenceTranscription,
  transcriptSentences,
  taskId,
  onAnalysisUpdated,
  readOnly = false,
}) => {
  // Hooks MUST run every render (Rules of Hooks). Do not return before hooks.
  const { hasPermission } = useAuth();
  const [overrideDialogOpen, setOverrideDialogOpen] = React.useState(false);
  const [selectedMetric, setSelectedMetric] = React.useState(null);

  const handleOverrideSuccess = () => {
    console.log('[AnalysisResults] Override success, calling onAnalysisUpdated...');
    onAnalysisUpdated?.();
    setOverrideDialogOpen(false);
    setSelectedMetric(null);
  };

  const analysis = analysisData?.analysis;
  
  // Debug: Log when analysis changes
  React.useEffect(() => {
    if (analysis) {
      console.log('[AnalysisResults] Analysis data updated:', {
        hasAnalysis: !!analysis,
        qa_metric_overrides: Object.keys(analysis.qa_metric_overrides || {}),
        ollama_qa_score: analysis.ollama_qa_score,
        metrics_count: analysis.ollama_qa_metric_scores?.length,
      });
    }
  }, [analysis?.qa_metric_overrides, analysis?.ollama_qa_score]);

  const sentiment = analysis?.sentiment_analysis || {};
  const criteriaType = analysis?.criteria_type || 'matrix';
  const isBooleanQA = criteriaType === 'boolean';

  // QA Scoring (Ollama only - ChatGPT deprecated)
  const qaMode = analysis?.qa_scoring_mode || 'ollama';
  // Recompute ollamaQA when analysis changes
  // Use override keys + metrics JSON to detect changes
  const overrideKeys = React.useMemo(() => {
    if (!analysis?.qa_metric_overrides) return '';
    return JSON.stringify(analysis.qa_metric_overrides);
  }, [analysis?.qa_metric_overrides]);

  const ollamaQA = React.useMemo(() => {
    if (!analysis) {
      return {
        score: null,
        categories: [],
        metrics: [],
        suggestions: [],
        processingTime: null,
      };
    }
    // Create new array references to ensure React detects changes
    // Backend should already enrich metrics with override info, but we ensure it here too
    const metrics = (analysis.ollama_qa_metric_scores || []).map(m => {
      const enriched = { ...m };
      // Ensure override info is present if override exists
      if (analysis?.qa_metric_overrides && m.metric_key && analysis.qa_metric_overrides[m.metric_key]) {
        enriched.is_overridden = true;
        enriched.override_info = analysis.qa_metric_overrides[m.metric_key];
        const ov = analysis.qa_metric_overrides[m.metric_key];
        const ovScore = ov?.overridden_score;
        if (ovScore != null) {
          enriched.score_value = Number(ovScore);
          enriched.score = Number(ovScore);
          if (enriched.weight_percentage != null) {
            enriched.weighted_score = Number(ovScore) * Number(enriched.weight_percentage) / 100.0;
          }
        }
        const ovRes = ov?.overridden_result;
        if (ovRes === 'Yes' || ovRes === 'No') {
          enriched.result = ovRes;
          enriched.passed = ovRes === 'Yes';
        }
      }
      return enriched;
    });
    const categories = (analysis.ollama_qa_category_scores || []).map(c => ({ ...c }));
    return {
      score: analysis.ollama_qa_score,
      categories,
      metrics,
      suggestions: analysis.ollama_improvement_suggestions || [],
      processingTime: analysis.ollama_qa_processing_time,
    };
  }, [
    analysis?.ollama_qa_score,
    JSON.stringify(analysis?.ollama_qa_category_scores),
    JSON.stringify(analysis?.ollama_qa_metric_scores),
    JSON.stringify(analysis?.qa_metric_overrides), // Add override dependency
    analysis?.ollama_improvement_suggestions,
    analysis?.ollama_qa_processing_time,
  ]);

  const hasOllamaQA = ollamaQA.score !== null && ollamaQA.score !== undefined;
  // ChatGPT deprecated - removed hasChatgptQA, hasBothQA, hasBothSentiment, hasBothCategories, hasBothSuggestions
  
  // Backward compatibility - use primary score if available
  const qaScoring = analysis?.qa_metric_scores || [];
  const categories = analysis?.qa_category_scores || [];
  const suggestions = analysis?.improvement_suggestions || [];

  // Boolean: mandatory items gate overall pass; optional items never block
  const qualificationItems = analysis?.qualification_items || [];
  const mandatoryItems = qualificationItems.filter((i) => i.is_mandatory);
  const derivedQualified =
    isBooleanQA && qualificationItems.length > 0
      ? mandatoryItems.length > 0
        ? mandatoryItems.every((i) => i.passed === true)
        : true
      : undefined;
  const effectiveQualified = derivedQualified !== undefined ? derivedQualified : !!analysis?.qualified;
  const booleanOnlyOptionalItems =
    isBooleanQA && qualificationItems.length > 0 && mandatoryItems.length === 0;

  if (!analysisData || !analysis) {
    return (
      <Alert severity="info">
        Analysis results not available yet. The task may still be processing.
      </Alert>
    );
  }

  return (
    <Box sx={{ 
      py: 2,
      // Hide any stray numbers or artifacts
      '& > div:empty': { display: 'none' },
      '& > span:empty': { display: 'none' }
    }}>
      {/* Boolean: Conversation Overview (standards + overall quality). Matrix: Sentiment Analysis */}
      {isBooleanQA ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <CheckCircle sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Conversation Overview</Typography>
          </Box>
          <Card variant="outlined" sx={{ backgroundColor: 'grey.50' }}>
            <CardContent>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Standards &amp; process
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                {booleanOnlyOptionalItems
                  ? 'This criteria has no mandatory qualification items. Overall status stays Qualified; use the list below for reference only.'
                  : effectiveQualified
                    ? 'The conversation met the required standards. The agent asked the required qualification questions and followed the process correctly.'
                    : 'The conversation did not meet the required standards. One or more mandatory qualification items were not satisfied (required questions not asked or not met).'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {booleanOnlyOptionalItems
                  ? 'Optional items do not change the overall Qualified / Not Qualified result.'
                  : effectiveQualified
                    ? 'Overall the conversation went well and the agent followed the correct process.'
                    : 'Review the qualification items below to see which mandatory items did not pass.'}
              </Typography>
            </CardContent>
          </Card>
        </Paper>
      ) : sentiment.ollama ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <EmojiEmotions sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">Sentiment Analysis</Typography>
          </Box>
          
          {/* Ollama Sentiment (ChatGPT deprecated) */}
          {sentiment.ollama && (
            <Box sx={{ width: '100%' }}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      <Psychology sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Ollama Analysis (Local)
                    </Typography>
                    <Chip 
                      label={`${(sentiment.ollama.confidence * 100).toFixed(0)}% confident`}
                      size="small"
                      color="secondary"
                      variant="outlined"
                    />
                  </Box>
                  
                  <Box display="flex" alignItems="center" mb={2}>
                    {getSentimentIcon(sentiment.ollama.label)}
                    <Box ml={2}>
                      <Typography variant="h5">
                        {sentiment.ollama.label?.toUpperCase()}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Score: {sentiment.ollama.score?.toFixed(2)} | {sentiment.ollama.emotion} ({sentiment.ollama.intensity})
                      </Typography>
                    </Box>
                  </Box>
                  
                  <Typography variant="caption" color="text.secondary">
                    {sentiment.ollama.reasoning}
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </Paper>
      ) : null}

      {/* QA Qualification (boolean) or QA Score Section (matrix - Ollama only) */}
      {isBooleanQA ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <CheckCircle sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">QA Qualification</Typography>
          </Box>
          <Box display="flex" alignItems="center" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
            <Chip
              icon={effectiveQualified ? <ThumbUp /> : <ThumbDown />}
              label={effectiveQualified ? 'Qualified' : 'Not Qualified'}
              color={effectiveQualified ? 'success' : 'error'}
              variant="filled"
              sx={{ fontWeight: 600 }}
            />
            {(analysis.qualified_count != null && analysis.qualification_total_items != null) && (
              <Typography variant="body2" color="text.secondary">
                {analysis.qualified_count} of {analysis.qualification_total_items} items passed
                {mandatoryItems.length > 0 ? (
                  <> • All {mandatoryItems.length} mandatory must pass to be Qualified</>
                ) : (
                  <> • No mandatory items — optional items do not change overall Qualified</>
                )}
              </Typography>
            )}
          </Box>
          {/* Qualification items summary with reference in conversation */}
          {Array.isArray(analysis.qualification_items) && analysis.qualification_items.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Stack spacing={2}>
                {analysis.qualification_items.map((item, idx) => (
                  <Card key={item.metric_key || idx} variant="outlined">
                    <CardContent>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ mb: 1 }}>
                        {item.passed ? (
                          <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />
                        ) : (
                          <Cancel sx={{ color: 'error.main', fontSize: 20 }} />
                        )}
                        <Typography variant="subtitle2" fontWeight={600}>
                          {item.name || item.metric_key || 'Item'}
                        </Typography>
                        {item.is_mandatory && (
                          <Chip label="Mandatory" size="small" color="warning" variant="outlined" />
                        )}
                        <Chip
                          label={item.result}
                          size="small"
                          color={item.result === 'Yes' ? 'success' : item.result === 'No' ? 'error' : 'default'}
                          variant="outlined"
                        />
                        {analysis?.qa_metric_overrides?.[item.metric_key]?.overridden_result != null && (
                          <Tooltip
                            title={
                              analysis.qa_metric_overrides[item.metric_key]?.reason
                                ? `Reason: ${analysis.qa_metric_overrides[item.metric_key].reason}`
                                : 'Overridden'
                            }
                          >
                            <Chip label="Overridden" size="small" variant="outlined" color="warning" />
                          </Tooltip>
                        )}
                        {taskId && !readOnly && hasPermission('QA_SCORE_OVERRIDE') && (
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => {
                              setSelectedMetric({
                                metric_key: item.metric_key,
                                metric_name: item.name || item.metric_key,
                                result: item.result,
                                is_overridden: Boolean(analysis?.qa_metric_overrides?.[item.metric_key]),
                                override_info: analysis?.qa_metric_overrides?.[item.metric_key],
                                overrideDialogVariant: 'boolean',
                              });
                              setOverrideDialogOpen(true);
                            }}
                            sx={{ textTransform: 'none', ml: 0.5 }}
                          >
                            Override qualification
                          </Button>
                        )}
                      </Box>
                      {item.question && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {item.question}
                        </Typography>
                      )}
                      {item.reasoning && (
                        <Typography variant="body2" sx={{ mb: 1.5, fontStyle: 'italic', color: 'text.secondary' }}>
                          {item.reasoning}
                        </Typography>
                      )}
                      {/* Evidence Display: Use enhanced evidence structure if available, fallback to evidence_sentences */}
                      <Box sx={{ mt: 1.5 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: 'block', mb: 1 }}>
                          Reference in conversation:
                        </Typography>
                        
                        {/* Use enhanced evidence array if available */}
                        {Array.isArray(item.evidence) && item.evidence.length > 0 ? (
                          <QualificationEvidenceDisplay
                            evidence={item.evidence}
                            onReferenceTranscription={onReferenceTranscription}
                          />
                        ) : (
                          /* Fallback to evidence_sentences for backward compatibility */
                          Array.isArray(item.evidence_sentences) && item.evidence_sentences.length > 0 ? (
                            <Stack spacing={1}>
                              {item.evidence_sentences
                                .map((num) => ({
                                  num,
                                  text: Array.isArray(transcriptSentences) && transcriptSentences[num - 1] != null
                                    ? transcriptSentences[num - 1]
                                    : null
                                }))
                                .filter((x) => x.text != null)
                                .map(({ num, text }) => (
                                  <Paper
                                    key={num}
                                    variant="outlined"
                                    sx={{ p: 1.5, backgroundColor: 'grey.50', borderColor: 'divider' }}
                                  >
                                    <Typography variant="caption" color="text.secondary" fontWeight={600}>
                                      Sentence {num}:
                                    </Typography>
                                    <Typography variant="body2" sx={{ mt: 0.5 }}>
                                      {text}
                                    </Typography>
                                  </Paper>
                                ))}
                            </Stack>
                          ) : (
                            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                              {item.status === 'insufficient_evidence'
                                ? (item.reasoning && String(item.reasoning).trim()
                                    ? `Insufficient evidence: ${String(item.reasoning).trim()}`
                                    : 'Insufficient evidence: No explicit evidence found in transcript')
                                : 'No reference available in evidence sentences'}
                            </Typography>
                          )
                        )}
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}
        </Paper>
      ) : hasOllamaQA ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Box display="flex" alignItems="center" mb={2}>
            <CheckCircle sx={{ mr: 1, color: 'primary.main' }} />
            <Typography variant="h6">QA Scoring</Typography>
          </Box>
          
          {/* Ollama QA Score (ChatGPT deprecated) */}
          {ollamaQA.score !== null && ollamaQA.score !== undefined && (
            <Box sx={{ width: '100%' }}>
              <Card variant="outlined">
                <CardContent>
                  <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="subtitle2" color="text.secondary">
                      <Psychology sx={{ fontSize: 16, mr: 0.5, verticalAlign: 'middle' }} />
                      Ollama QA Scoring (Local)
                    </Typography>
                    {ollamaQA.processingTime && (
                      <Chip 
                        label={`${ollamaQA.processingTime ? ollamaQA.processingTime.toFixed(1) : '0'}s • FREE`}
                        size="small"
                        color="secondary"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  
                  <Typography variant="h3" color={getScoreColor(ollamaQA.score)} gutterBottom>
                    {ollamaQA.score != null ? ollamaQA.score.toFixed(1) : 'N/A'}/100
                  </Typography>
                  
                  <LinearProgress 
                    variant="determinate" 
                    value={ollamaQA.score} 
                    sx={{ 
                      height: 10, 
                      borderRadius: 5,
                      mb: 1,
                      backgroundColor: 'grey.200',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: getScoreColor(ollamaQA.score)
                      }
                    }} 
                  />
                  
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    {getScoreLabel(ollamaQA.score)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', opacity: 0.9 }}>
                    This is the score from <strong>your QA checklist</strong> (how the call matched each standard).
                    It is <strong>not</strong> the same as the short transcript checks at the bottom of this page.
                  </Typography>
                </CardContent>
              </Card>
            </Box>
          )}
        </Paper>
      ) : null}

      {/* QA Category Breakdown (matrix) - Ollama only (ChatGPT deprecated) */}
      {!isBooleanQA && ollamaQA.categories && ollamaQA.categories.length > 0 ? (
        <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" mb={2}>📊 QA Category Breakdown</Typography>
          
          {/* Ollama Categories (ChatGPT deprecated) */}
          {ollamaQA.categories && ollamaQA.categories.length > 0 && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <Psychology sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                Ollama Analysis (Local & Free)
              </Typography>
              {ollamaQA.categories.map((category, idx) => (
                <Accordion key={`ollama-${idx}`} defaultExpanded={idx === 0}>
                  <AccordionSummary expandIcon={<ExpandMore />}>
                    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 2 }}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {category.category_name}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          Weight: {category.weight_percentage}% • {category.metrics_count} metrics
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${category.score ? category.score.toFixed(1) : 0}/${category.max_score || 0} (${category.percentage ? category.percentage.toFixed(0) : 0}%)`}
                        color={category.percentage >= 80 ? 'success' : category.percentage >= 60 ? 'warning' : 'error'}
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>
                  
                  <AccordionDetails>
                    <LinearProgress 
                      variant="determinate" 
                      value={category.percentage || 0} 
                      sx={{ mb: 2, height: 6, borderRadius: 3 }}
                      color={category.percentage >= 80 ? 'success' : category.percentage >= 60 ? 'warning' : 'error'}
                    />
                    
                    {/* Find metrics for this category */}
                    {ollamaQA.metrics
                      .filter((m) => metricBelongsToOllamaCategory(m, category))
                      .map((metric) => {
                        // Enrich metric with override info from analysis if not already present
                        const enrichedMetric = { ...metric };
                        if (analysis?.qa_metric_overrides && metric.metric_key) {
                          const overrideData = analysis.qa_metric_overrides[metric.metric_key];
                          if (overrideData) {
                            enrichedMetric.is_overridden = true;
                            enrichedMetric.override_info = overrideData;
                            if (overrideData.overridden_score != null) {
                              const ovScore = Number(overrideData.overridden_score);
                              enrichedMetric.score_value = ovScore;
                              enrichedMetric.score = ovScore;
                              // Recalculate weighted_score when overridden
                              if (enrichedMetric.weight_percentage != null) {
                                enrichedMetric.weighted_score = ovScore * Number(enrichedMetric.weight_percentage) / 100.0;
                              }
                            }
                          } else {
                            // Debug: log when metric_key exists but no override found
                            const overrideKeys = Object.keys(analysis.qa_metric_overrides || {});
                            if (overrideKeys.length > 0) {
                              console.log(`[AnalysisResults] Metric "${metric.metric_name}" (key: ${metric.metric_key}) not found in overrides. Available keys:`, overrideKeys);
                            }
                          }
                        }
                        const overrideKey = enrichedMetric.is_overridden && enrichedMetric.override_info
                          ? `${enrichedMetric.override_info.overridden_score}-${enrichedMetric.override_info.overridden_at || ''}`
                          : 'none';
                        return (
                          <MetricCard
                            key={`${category.category_id}-${enrichedMetric.metric_key || enrichedMetric.metric_name || 'unknown'}-${overrideKey}`}
                            metric={enrichedMetric}
                            onReferenceTranscription={onReferenceTranscription}
                            taskId={taskId}
                            readOnly={readOnly}
                            onOverrideClick={(m) => {
                              const isBooleanMetric = m.result !== undefined;
                              setSelectedMetric({
                                ...m,
                                overrideDialogVariant: isBooleanMetric ? 'boolean' : 'matrix',
                              });
                              setOverrideDialogOpen(true);
                            }}
                          />
                        );
                      })}
                  </AccordionDetails>
                </Accordion>
              ))}
            </Box>
          )}
        </Paper>
      ) : null}

      {/* Improvement Suggestions (Ollama only - ChatGPT deprecated) */}
      {ollamaQA.suggestions && ollamaQA.suggestions.length > 0 ? (
        <Paper elevation={2} sx={{ p: 3 }}>
          <Typography variant="h6" mb={2}>💡 Improvement Suggestions</Typography>
          
          {/* Ollama Suggestions (ChatGPT deprecated) */}
          {ollamaQA.suggestions && ollamaQA.suggestions.length > 0 && (
            <Box sx={{ width: '100%' }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                <Psychology sx={{ fontSize: 14, mr: 0.5, verticalAlign: 'middle' }} />
                Ollama Suggestions (Local & Free)
              </Typography>
              {ollamaQA.suggestions.map((suggestion, idx) => (
                <Alert 
                  key={idx}
                  severity={suggestion.priority === 'high' ? 'error' : suggestion.priority === 'medium' ? 'warning' : 'info'}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="body2" fontWeight="bold">
                    {suggestion.category} - {suggestion.metric}
                  </Typography>
                  <Typography variant="caption" display="block">
                    {suggestion.suggestion}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Impact: {suggestion.impact || 'N/A'} • Current: {suggestion.current_score || 'N/A'}
                  </Typography>
                </Alert>
              ))}
            </Box>
          )}
        </Paper>
      ) : null}

      {/* Transcript heuristics only (word length / fixed signals) — NOT ollama_qa_score */}
      {(analysis.quality_score || analysis.clarity_score) && (
        <Paper elevation={1} sx={{ p: 2, mt: 3, bgcolor: 'grey.50' }}>
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            About the transcript (extra info)
          </Typography>
          <Alert severity="info" icon={<InfoIcon />} sx={{ mb: 2, py: 0.5 }}>
            <Typography variant="body2" component="div" sx={{ mb: 0.5 }}>
              These numbers only describe the <strong>text of the call</strong> after it was converted from audio —
              for example how long the transcript is.
            </Typography>
            <Typography variant="caption" component="div" color="text.secondary">
              They do <strong>not</strong> rate service quality or how well the agent followed your QA standards.
              For that, use <strong>{isBooleanQA ? 'QA Qualification' : 'QA Scoring'}</strong> at the top of this tab.
            </Typography>
          </Alert>
          <Stack direction="row" spacing={2} flexWrap="wrap">
            {analysis.quality_score && (
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '120px' }}>
                <Typography variant="caption" color="text.secondary">
                  Overall transcript score
                </Typography>
                <Typography variant="h6">{analysis.quality_score.toFixed(1)}/100</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  A simple blend of the three checks below — for reference only
                </Typography>
              </Box>
            )}
            {analysis.clarity_score && (
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '120px' }}>
                <Typography variant="caption" color="text.secondary">How much was said</Typography>
                <Typography variant="h6">{(analysis.clarity_score * 100).toFixed(0)}%</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Based on amount of wording in the transcript
                </Typography>
              </Box>
            )}
            {analysis.coherence_score && (
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '120px' }}>
                <Typography variant="caption" color="text.secondary">Flow of the text</Typography>
                <Typography variant="h6">{(analysis.coherence_score * 100).toFixed(0)}%</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Part of the automatic mix — not a human review of the conversation
                </Typography>
              </Box>
            )}
            {analysis.completeness_score && (
              <Box sx={{ flex: '1 1 calc(50% - 8px)', minWidth: '120px' }}>
                <Typography variant="caption" color="text.secondary">How full the transcript looks</Typography>
                <Typography variant="h6">{(analysis.completeness_score * 100).toFixed(0)}%</Typography>
                <Typography variant="caption" color="text.secondary" display="block">
                  Based on overall length of the transcript
                </Typography>
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {/* Score Override Dialog */}
      {!readOnly && selectedMetric && (
        <ScoreOverrideDialog
          key={`override-dialog-${selectedMetric.metric_key || 'unknown'}`}
          open={overrideDialogOpen}
          onClose={() => {
            setOverrideDialogOpen(false);
            setSelectedMetric(null);
          }}
          metric={selectedMetric}
          taskId={taskId}
          variant={selectedMetric.overrideDialogVariant === 'boolean' ? 'boolean' : 'matrix'}
          onSuccess={handleOverrideSuccess}
        />
      )}
    </Box>
  );
};

// Matrix bands (evidence-based + legacy prompts)
const matrixBandChipColor = (band) => {
  const b = String(band || '').toLowerCase();
  if (b === 'excellent' || b === 'good') return 'success';
  if (b === 'fair') return 'warning';
  if (b === 'poor') return 'error';
  return 'default';
};

// Helper component for metric cards (supports matrix score and boolean result)
const MetricCard = ({
  metric,
  onReferenceTranscription,
  taskId,
  onOverrideClick,
  readOnly = false,
}) => {
  const { hasPermission } = useAuth();
  
  // Check if user can override scores (team manager or QA_SCORE_OVERRIDE permission)
  // Note: Team manager check would need to be done server-side, so we only check permission here
  const canOverride = !readOnly && hasPermission('QA_SCORE_OVERRIDE');
  
  const isBoolean = metric.result !== undefined;
  const showOverrideButton = canOverride && taskId;

  const isOverridden = Boolean(
    metric.is_overridden === true ||
    (metric.override_info && typeof metric.override_info === 'object' &&
      (metric.override_info.overridden_score != null || metric.override_info.overridden_result != null)) ||
    (metric.qa_metric_overrides && metric.metric_key && metric.qa_metric_overrides[metric.metric_key])
  );
  const overrideInfo = metric.override_info ||
    (metric.qa_metric_overrides && metric.metric_key && metric.qa_metric_overrides[metric.metric_key]) ||
    null;
  const displayValue = isBoolean ? metric.result : metric.score;
  const matrixBand = !isBoolean && typeof metric.score === 'string' ? metric.score : '';
  const matrixNumeric =
    !isBoolean && !matrixBand
      ? Number(metric.score_value != null ? metric.score_value : metric.score)
      : null;
  const numericOk = matrixNumeric != null && !Number.isNaN(matrixNumeric);
  const isPass = isBoolean
    ? metric.result === 'Yes'
    : matrixBand
      ? matrixBand === 'excellent' || matrixBand === 'good'
      : numericOk
        ? matrixNumeric >= 60
        : metric.score === 'Met (1)';
  const isFail = isBoolean
    ? metric.result === 'No'
    : matrixBand
      ? matrixBand === 'poor'
      : numericOk
        ? matrixNumeric < 40
        : metric.score === 'Unmet (0)';
  const borderColor = isPass ? 'success.main' : isFail ? 'error.main' : 'grey.400';
  const icon = isPass ? <CheckCircle color="success" fontSize="small" /> : isFail ? <Cancel color="error" fontSize="small" /> : <InfoIcon color="action" fontSize="small" />;
  const chipColor = isBoolean
    ? (isPass ? 'success' : isFail ? 'error' : 'default')
    : matrixBand
      ? matrixBandChipColor(matrixBand)
      : isPass
        ? 'success'
        : isFail
          ? 'error'
          : 'default';
  const evidenceItems = Array.isArray(metric.evidence_items) ? metric.evidence_items : [];
  const isMatrixEvidenceBased = metric.analysis_mode === 'matrix_evidence_based';

  const hasNumericScore = !isBoolean && (metric.score_value != null || metric.weighted_score != null);
  const maxWeighted = metric.weight_percentage != null ? Number(metric.weight_percentage) : 0;
  const weightedScore = metric.weighted_score != null ? Number(metric.weighted_score) : null;
  const scoreValue = metric.score_value != null ? Number(metric.score_value) : null;
  
  // Use override reason if metric is overridden, otherwise use original reasoning
  const displayReasoning = isOverridden && overrideInfo?.reason 
    ? overrideInfo.reason 
    : metric.reasoning || metric.evidence?.reasoning || '';

  return (
    <Card sx={{ mb: 2, borderLeft: 4, borderColor }} variant="outlined">
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
          <Box display="flex" alignItems="center" flex={1}>
            {icon}
            <Box ml={1}>
              <Typography variant="subtitle2">
                {metric.metric_name}{!isBoolean && metric.weight_percentage != null ? ` (${metric.weight_percentage}%)` : ''}
              </Typography>
              {hasNumericScore && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                  {maxWeighted > 0 && weightedScore != null && (
                    <span>Score: {weightedScore.toFixed(1)}/{maxWeighted}</span>
                  )}
                  {scoreValue != null && (
                    <span>{maxWeighted > 0 && weightedScore != null ? ` (${scoreValue.toFixed(0)}/100)` : `Score: ${scoreValue.toFixed(0)}/100`}</span>
                  )}
                </Typography>
              )}
            </Box>
          </Box>
          <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap">
            <Chip label={displayValue} color={chipColor} size="small" />
            {isMatrixEvidenceBased && (
              <Chip label="Evidence-based" size="small" variant="outlined" color="primary" />
            )}
            {isOverridden && (
              <Tooltip title={`Overridden by ${overrideInfo?.overridden_by || 'user'} on ${overrideInfo?.overridden_at ? new Date(overrideInfo.overridden_at).toLocaleString() : 'unknown date'}. Reason: ${overrideInfo?.reason || 'N/A'}`}>
                <Chip label="Overridden" size="small" variant="outlined" color="warning" />
              </Tooltip>
            )}
            {showOverrideButton && (
              <Button
                size="small"
                variant="outlined"
                onClick={() => onOverrideClick(metric)}
                sx={{ 
                  ml: 0.5,
                  minWidth: 'auto',
                  px: 1,
                  py: 0.25,
                  fontSize: '0.75rem',
                  textTransform: 'none'
                }}
              >
                {isBoolean ? 'Override qualification' : 'Override score'}
              </Button>
            )}
          </Stack>
        </Box>

        {/* Matrix: mapped evidence items (original + English) */}
        {evidenceItems.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <QualificationEvidenceDisplay
              evidence={evidenceItems}
              onReferenceTranscription={onReferenceTranscription}
            />
          </Box>
        )}

        {/* Evidence Section (legacy numbered sentences) */}
        {metric.evidence && metric.evidence.sentences && metric.evidence.sentences.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Evidence from transcription:
            </Typography>
            
            {metric.evidence.sentences.map((sent, idx) => (
              <Paper 
                key={idx}
                sx={{ 
                  p: 1.5, 
                  mt: 1, 
                  backgroundColor: isPass ? 'success.50' : 'warning.50',
                  border: 1,
                  borderColor: isPass ? 'success.200' : 'warning.200'
                }}
              >
                <Typography variant="caption" color="text.secondary">
                  Sentence #{sent.number}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {sent.text}
                </Typography>
                {sent.quote && (
                  <Chip 
                    label={`Key: "${sent.quote}"`} 
                    size="small" 
                    sx={{ mt: 0.5 }}
                  />
                )}
              </Paper>
            ))}
          </Box>
        )}

        {/* Reasoning - Show override reason if overridden, otherwise show AI reasoning */}
        {displayReasoning && (
          <Alert 
            severity={isOverridden ? 'info' : (isPass ? 'success' : 'warning')}
            sx={{ mt: 2 }}
            icon={false}
          >
            <Typography variant="caption">
              {isOverridden ? '📝 Override Reason: ' : '💭 '}
              {displayReasoning}
            </Typography>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};

// Helper functions
const getSentimentIcon = (label) => {
  switch (label?.toLowerCase()) {
    case 'positive':
      return <ThumbUp sx={{ fontSize: 40, color: 'success.main' }} />;
    case 'negative':
      return <ThumbDown sx={{ fontSize: 40, color: 'error.main' }} />;
    case 'neutral':
    default:
      return <TrendingFlat sx={{ fontSize: 40, color: 'grey.500' }} />;
  }
};

const getScoreColor = (score) => {
  if (score >= 80) return 'success.main';
  if (score >= 60) return 'warning.main';
  return 'error.main';
};

const getScoreLabel = (score) => {
  if (score >= 90) return 'Excellent - Outstanding performance';
  if (score >= 80) return 'Very Good - Above expectations';
  if (score >= 70) return 'Good - Meets expectations';
  if (score >= 60) return 'Satisfactory - Needs minor improvements';
  if (score >= 50) return 'Below Average - Needs improvement';
  return 'Poor - Requires significant improvement';
};

export default AnalysisResults;
